import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RiMenu3Line, RiCloseLine } from "react-icons/ri";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import { getSubjectMeta } from "../../constants/subjectMeta";

import {
  StudentNavbar,
  SubjectSideNavigation,
  LoadingComponent,
  NotFound,
  ModuleQuiz,
} from "../../components";

const ModuleLearn = () => {
  const { exam_type, subject, module } = useParams();
  const examSlug = (exam_type || "bgcse").toLowerCase();
  const navigate = useNavigate();
  const subjectMeta = getSubjectMeta(examSlug, subject);
  const subjectImage = subjectMeta?.image;

  const [subjectData, setSubjectData] = useState(null);
  const [subjectModule, setSubjectModule] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const randomLimit = Number(qs.get("random")) || 0;
  const mode = qs.get("mode") || null;
  const isQuickMode = mode === "quick" && randomLimit > 0;

  // Mobile dropdown state (same pattern as Biology page)
  const [showModuleMenu, setShowModuleMenu] = useState(false);

  const deslugify = (slug = "") =>
    slug
      .split("-")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");

  const normalizedSubjectName = deslugify(subject);

  const exitQuickStudy = () => {
    navigate(`/${examSlug}/${subject}/${module}/learn`, { replace: true });
  };

  // 1) Fetch subject
  useEffect(() => {
    const fetchSubject = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/subjects`, {
          params: {
            subjectName: normalizedSubjectName,
            examType: examSlug,
          },
        });

        const firstSubject = res.data[0];
        if (!firstSubject) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setSubjectData(firstSubject);
      } catch (err) {
        console.error("Subject lookup failed", err);
        setNotFound(true);
        setLoading(false);
      }
    };

    // reset state when subject slug changes
    setLoading(true);
    setSubjectData(null);
    setSubjectModule(null);
    setQuestions([]);
    setNotFound(false);
    setShowModuleMenu(false);

    fetchSubject();
  }, [normalizedSubjectName, examSlug]);

  // 2) Fetch the specific module for that subject (using the slug from the URL)
  useEffect(() => {
    if (!subjectData) return;

    const fetchModule = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/subject_modules`, {
          params: {
            subjectId: subjectData.id,
          },
        });

        const formattedModuleName = module.replace(/-/g, " ").toLowerCase();

        const foundModule = (res.data || []).find(
          (m) => m.module_name.toLowerCase() === formattedModuleName
        );

        if (!foundModule) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setSubjectModule(foundModule);
      } catch (err) {
        console.error("Module lookup failed", err);
        setNotFound(true);
        setLoading(false);
      }
    };

    fetchModule();
  }, [subjectData, module]);

  // 3) Fetch questions for that module
  useEffect(() => {
    if (!subjectModule) return;

    const fetchQuestions = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/questions`, {
          params: {
            module: subjectModule.id,
          },
        });

        setQuestions(res.data || []);
      } catch (err) {
        console.error("Question load failed", err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [subjectModule]);

  if (notFound) return <NotFound />;

  if (loading || !subjectData || !subjectModule) {
    return <LoadingComponent />;
  }

  return (
    <div className="w-full min-h-screen">
      <StudentNavbar />

      <div className="bg-white w-full">
        {/* Responsive grid: 1 col on small screens, 5 cols on lg+ */}
        <div className="grid grid-cols-1 lg:grid-cols-5">
          {/* Side Navigation: hidden on mobile/tablet, visible on lg+ */}
          <div className="hidden lg:block lg:col-span-1 text-sm border-r border-gray-200">
            <SubjectSideNavigation
              selectedModuleIndex={subjectModule.module_number}
              subjectName={normalizedSubjectName}
              img={subjectImage}
            />
          </div>

          {/* Main Content */}
          <div className="col-span-1 lg:col-span-4 bg-[#f0f0f0] min-h-screen px-3 sm:px-5 py-3 sm:py-5 lg:h-[calc(100vh-100px)] overflow-y-auto">
            {/* Mobile Module Dropdown Button */}
            <div className="lg:hidden mb-4">
              <button
                onClick={() => setShowModuleMenu(!showModuleMenu)}
                className="w-full bg-white rounded-lg shadow-sm px-4 py-3 flex items-center justify-between text-gray-700 hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={subjectImage}
                    alt={normalizedSubjectName}
                    className="h-8 w-8 rounded object-cover"
                  />
                  <div className="flex flex-col items-start leading-tight">
                    <span className="font-semibold">{normalizedSubjectName} Modules</span>
                    <span className="text-xs text-gray-500">
                      Currently: {subjectModule.module_name}
                    </span>
                  </div>
                </div>

                {showModuleMenu ? (
                  <RiCloseLine size={24} className="text-gray-600" />
                ) : (
                  <RiMenu3Line size={24} className="text-gray-600" />
                )}
              </button>

              {/* Dropdown Menu */}
              {showModuleMenu && (
                <div className="mt-2 bg-white rounded-lg shadow-lg overflow-hidden animate-scale-down">
                  <SubjectSideNavigation
                    selectedModuleIndex={subjectModule.module_number}
                    subjectName={normalizedSubjectName}
                    img={subjectImage}
                  />
                </div>
              )}
            </div>

            {/* Module Banner */}
            <div className="mb-2 rounded-2xl border border-white/60 bg-white/70 backdrop-blur-md shadow-sm overflow-hidden">
              <div className="px-4 sm:px-6 py-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-gray-500 whitespace-nowrap">
                    Module {subjectModule.module_number}
                  </p>

                  <a
                    href={`/${examSlug}/${subject}`}
                    className="shrink-0 whitespace-nowrap text-xs font-semibold text-sky-700 hover:text-sky-900 hover:underline"
                  >
                    ← {normalizedSubjectName} Home
                  </a>
                </div>

                {/* Row 2: module name */}
                <h1 className="mt-1 text-sm sm:text-base tracking-[0.1em] text-gray-900 leading-tight break-words">
                  {subjectModule.module_name}
                </h1>

                {/* Row 3: Quick-study exit only*/}
                {isQuickMode ? (
                  <div className="flex items-center mt-3 md:mt-0 md:justify-end">
                    <button
                      type="button"
                      onClick={exitQuickStudy}
                      className="text-[11px] font-semibold text-gray-700 hover:text-gray-900 hover:underline hover:underline-offset-2"
                    >
                      Switch to Full Study
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Quiz area */}
            <ModuleQuiz
              questions={questions}
              subjectModuleId={subjectModule.id}
              moduleNumber={subjectModule.module_number}
              randomLimit={randomLimit}
              mode={mode}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scale-down {
          0% {
            transform: scaleY(0);
            opacity: 0;
            transform-origin: top;
          }
          100% {
            transform: scaleY(1);
            opacity: 1;
            transform-origin: top;
          }
        }
        .animate-scale-down {
          animation: scale-down 0.2s ease-out both;
        }
      `}</style>
    </div>
  );
};

export default ModuleLearn;
